/**
 * Glue layer: typed access to the generated JSONs (src/data) and adaptation
 * to the engine's parametric inputs. The engine itself never imports data.
 */
import teamsJson from "../data/teams.json";
import scheduleJson from "../data/schedule.json";
import bracketJson from "../data/bracket.json";
import modelParamsJson from "../data/model-params.json";
import gkRatingsJson from "../data/gk-ratings.json";
import type { BracketNodes, Slot } from "../engine/bracket.ts";
import type { TournamentStructure, SimGroupMatch, SimKoMatch } from "../engine/montecarlo.ts";
import type { GkTable, ShootoutModel } from "../engine/shootout.ts";
import type { EloTable, HostsTable, PoissonParams } from "../engine/types.ts";

export interface TeamRecord {
  readonly id: string;
  readonly nameFr: string;
  readonly flagCode: string;
  readonly group: string;
  /** Authoritative rating: eloratings.net at 2026-06-11 (+ declared adjustments). */
  readonly elo: number;
  /** Raw legacy model value (kept for transparency/continuity). */
  readonly eloLegacy: number;
  readonly eloSource: string;
  readonly eloFlags: readonly string[];
  readonly eloAdjustment: { readonly delta: number; readonly reason: string } | null;
}

export const teams: readonly TeamRecord[] = teamsJson.teams;

export const initialElos: EloTable = Object.fromEntries(teams.map((t) => [t.id, t.elo]));

/** Legacy v2 Elo values (the original simulation's ratings) — for the model picker. */
export const legacyElos: EloTable = Object.fromEntries(teams.map((t) => [t.id, t.eloLegacy]));

/** Starting-goalkeeper quality (EA FC 26) — partial coverage, engine falls back to 50/50. */
export const gkRatings: GkTable = Object.fromEntries(
  Object.entries(gkRatingsJson.ratings).map(([code, r]) => [code, r.rating]),
);

export const shootoutModel: ShootoutModel = modelParamsJson.shootout.model as ShootoutModel;
export const gkDivisor: number = modelParamsJson.shootout.gkRatingDivisor;

export const hosts: HostsTable = { MEX: "mx", USA: "us", CAN: "ca" };

export const groups: Readonly<Record<string, readonly string[]>> = (() => {
  const out: Record<string, string[]> = {};
  for (const t of teams) (out[t.group] ??= []).push(t.id);
  return out;
})();

export const poissonParams: PoissonParams = {
  baseTotalGoals: modelParamsJson.poisson.baseTotalGoals,
  totalGoalsEloDivisor: modelParamsJson.poisson.totalGoalsEloDivisor,
  lambdaMin: modelParamsJson.poisson.lambdaMin,
  shareEloDivisor: modelParamsJson.poisson.shareEloDivisor,
  maxGoalsPerTeam: modelParamsJson.poisson.maxGoalsPerTeam,
};

/** Legacy model home advantage (+90) — for the Poisson mapping only. */
export const modelHomeAdvantage: number = modelParamsJson.homeAdvantageElo.legacyModel;

export interface ScheduleMatch {
  readonly id: number;
  readonly stage: string;
  readonly group: string | null;
  readonly matchday: number | null;
  readonly home: string;
  readonly away: string;
  readonly utcDateTime: string;
  readonly stadium: string | null;
  readonly stadiumFifa: string | null;
  readonly city: string | null;
  readonly hostCountry: string | null;
  readonly timezone: string | null;
  readonly venueId: string | null;
}

export const schedule: readonly ScheduleMatch[] = scheduleJson.matches;

export function buildTournamentStructure(): TournamentStructure {
  const groupMatches: SimGroupMatch[] = schedule
    .filter((m) => m.stage === "group" && m.group !== null)
    .map((m) => ({
      id: m.id,
      group: m.group as string,
      home: m.home,
      away: m.away,
      hostCountry: m.hostCountry,
    }));
  const nodes = bracketJson.nodes as unknown as BracketNodes;
  const koMatches: SimKoMatch[] = Object.entries(bracketJson.nodes).map(([nodeId, node]) => ({
    nodeId,
    id: Number(nodeId.slice(1)),
    hostCountry: (node as { hostCountry: string | null }).hostCountry,
  }));
  return {
    groups,
    groupMatches,
    koMatches,
    r32Sources: bracketJson.r32Sources as unknown as Readonly<
      Record<string, readonly [string, string]>
    >,
    thirdsTable: {
      allowedGroups: bracketJson.thirdsAllowedGroups,
      slotOrder: bracketJson.thirdsSlotOrder,
    },
    nodes,
    hosts,
  };
}

export type { Slot };
